import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Printer, Download } from "lucide-react";
import { downloadPdf } from "../lib/pdf";
import { api } from "../lib/api";
import { clienteName, eur, fmtDate } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Targa } from "../components/Targa";
import { StatoBadge, TipoBadge } from "../components/Badges";
import { GaranziaDialog } from "../components/GaranziaDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATI = [["", "Tutte"], ["attiva", "Attive"], ["in_scadenza", "In scadenza"], ["scaduta", "Scadute"]];

export default function Garanzie() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const stato = params.get("stato") || "";
  const [open, setOpen] = useState(params.get("nuova") === "1");

  const load = () => api.get("/garanzie", { params: { q, tipo, stato } }).then((r) => setItems(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, tipo, stato]); // eslint-disable-line

  return (
    <div>
      <PageHeader title="Registro garanzie" subtitle="Garanzie legali di conformità e garanzie commerciali">
        <Button onClick={() => setOpen(true)} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="new-garanzia-btn"><Plus className="mr-1 h-4 w-4" /> Nuova garanzia</Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca numero, targa, telaio, cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-9" data-testid="garanzie-search-input" />
        </div>
        <Tabs value={tipo || "all"} onValueChange={(v) => setTipo(v === "all" ? "" : v)}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-tipo-all">Tutti i tipi</TabsTrigger>
            <TabsTrigger value="conformita" data-testid="filter-tipo-conformita">Conformità</TabsTrigger>
            <TabsTrigger value="commerciale" data-testid="filter-tipo-commerciale">Commerciali</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={stato || "all"} onValueChange={(v) => setParams(v === "all" ? {} : { stato: v })}>
          <TabsList>
            {STATI.map(([v, l]) => <TabsTrigger key={v || "all"} value={v || "all"} data-testid={`filter-stato-${v || "all"}`}>{l}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>
      <div className="overflow-x-auto rounded-md border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>N°</TableHead><TableHead>Targa</TableHead><TableHead>Cliente / Veicolo</TableHead><TableHead>Tipo</TableHead><TableHead>Periodo</TableHead><TableHead>Prezzo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Contratto</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Nessuna garanzia trovata</TableCell></TableRow>}
            {items.map((g) => (
              <TableRow key={g.id} className="cursor-pointer" data-testid={`garanzia-row-${g.id}`}>
                <TableCell><Link to={`/garanzie/${g.id}`} className="font-mono text-xs font-bold text-[#B08D57] hover:underline" data-testid={`garanzia-link-${g.id}`}>{g.numero}</Link></TableCell>
                <TableCell><Targa value={g.veicolo?.targa} /></TableCell>
                <TableCell><div className="font-semibold">{clienteName(g.cliente)}</div><div className="text-xs text-muted-foreground">{g.veicolo?.marca} {g.veicolo?.modello}</div></TableCell>
                <TableCell><TipoBadge g={g} /></TableCell>
                <TableCell className="whitespace-nowrap text-xs">{fmtDate(g.data_inizio)} → <b>{fmtDate(g.data_fine)}</b></TableCell>
                <TableCell className="text-sm">{g.tipo === "commerciale" ? eur(g.prezzo) : "—"}</TableCell>
                <TableCell><StatoBadge stato={g.stato} testId={`garanzia-stato-${g.id}`} /></TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button size="icon" variant="ghost" asChild title="Stampa contratto" data-testid={`print-garanzia-${g.id}`}>
                    <Link to={`/garanzie/${g.id}/certificato`}><Printer className="h-4 w-4" /></Link>
                  </Button>
                  <Button size="icon" variant="ghost" title="Scarica PDF" onClick={() => downloadPdf(g.id, g.numero)} data-testid={`download-garanzia-${g.id}`}>
                    <Download className="h-4 w-4 text-[#B08D57]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <GaranziaDialog open={open} onClose={() => { setOpen(false); if (params.get("nuova")) setParams({}); }} onSaved={load} />
    </div>
  );
}
